import { ITab } from '../../config/index.js';
import { ApplicationStatusTotals } from '../application-summary/index.js';
import { ApplicationStatus, ApplyDocTypes, FeeStatus } from '../enums.js';
import { OfferStatusTotals } from '../offer-summary/index.js';
import {
  ApplicantOfferRequirementTask,
  ApplicantOfferRequirementTaskLevel,
  ApplicantOfferStatus,
} from '../offer/enums.js';
import { ApplyScholarship } from '../offer/index.js';
import { ICourseRequirementRule } from './courseRequirementRules.js';
import { ApplicationTaskSchema } from './validation.js';

export type ConditionTextValues = {
  [key in ApplicantOfferRequirementTask]: string[];
};

export interface IApplyUniConfig {
  privacyPolicyConsentWording: ConsentWording;
  termsAndConditionConsentWording: ConsentWording;
  privacyPolicyUrl: string;
  termsAndConditionsUrl: string;
  depositText?: string;
  offerLetterStartTemplate: string;
  offerLetterEndTemplate: string;
  scholarships: ApplyScholarship[];
  welcomeScreen: WelcomeScreen;

  disableFundingTuitionFeeQuestion?: boolean; // Hide SLC tuition fee question
  disableFundingMaintenanceQuestion?: boolean; // Hide SLC maintenance loan question
  disableFundingWorkQuestion?: boolean; // Hide work throughout studies question
  disableDeclarationInterruptionQuestion?: boolean; // Hide interruption question
  disableDeclarationConvictionQuestion?: boolean; // Hide criminal conviction question (replaces disabledSections.criminalRecord)
  /** @deprecated Use APPLICATION_TASKS config instead */
  disableDeclarationTask?: boolean; // Legacy field to disable entire Declarations task
  rejectionReasons?: RejectionReasonTemplates;
  offerLetterConfig?: Record<FeeStatus, TemplateByStatus>;
  customSubdomain?: string;
  sslCertificateArn?: string;
  addressLookupEnabled?: boolean;
  addressLookupConfig?: AddressLookupConfig;
}

export interface AddressLookupConfig {
  permanentAddressLookupEnabled: boolean;
  contactAddressLookupEnabled: boolean;
  termAddressLookupEnabled: boolean;
  ukAddressLookupEnabled: boolean;
  employmentAddressLookupEnabled: boolean;
  refereeAddressLookupEnabled: boolean;
  educationAddressLookupEnabled: boolean;
}

export interface RejectionReasonTemplates {
  entryCriteriaNotMet: string;
  failedAcademicAssessment: string;
  failedCredibilityAssessment: string;
  fraudulentApplication: string;
  courseClosure: string;
  courseFull: string;
  unableToOfferCourse: string;
  deadlineHasPassed: string;
  closedByApplicant: string;
  wrongApplicationForm: string;
  failedPreCasInterview: string;
  failedComplianceInterview: string;
  negativeQualificationVerification: string;
  duplicateApplication: string;
  inactiveApplication: string;
  regionExclusiveToAgent: string;
  other: string;
}

/**
 * Example: HOME: {
 *     Conditional: 'templateId1',
 *     Unconditional: 'templateId2'
 *   },
 */
export type TemplateByStatus = Record<ApplicantOfferStatus, string>;

export type IApplyDashboardTabs = Record<string, ITab[]>;
export type IApplyDashboardDocumentConfig = Record<string, ApplyDocTypes[]>;
export type IApplyDashboardTabStatusMap = Record<
  string,
  { offerStatuses: ApplicantOfferStatus[]; applicationStatuses: ApplicationStatus[] }
>;

export interface IApplyDashboardTabConfig {
  [userRole: string]: {
    tabs: IApplyDashboardTabs;
    documentMap: IApplyDashboardDocumentConfig;
    map: IApplyDashboardTabStatusMap;
  };
}

export interface IApplyDashboardRowTotals {
  offerTotals: OfferStatusTotals;
  applicationTotals: ApplicationStatusTotals;
}

export interface ConsentWording {
  subtitle: string;
  header: string;
  bodyText: string;
  buttonLabel: string;
}

export interface WelcomeScreen {
  title: string;
  content: string;
}

export interface IApplyUniConfigRequirement {
  id: string;
  enabled: boolean;
  fulfilmentMethod: UniConfigRequirementTaskFulfilmentMethod;
  type: ApplicantOfferRequirementTask;
  taskText?: string;
  label: string;
  defaultLevel: ApplicantOfferRequirementTaskLevel;
  staffOnly?: boolean;
  conditions: {
    default?: string;
    [FeeStatus.Home]?: string;
    [FeeStatus.Overseas]?: string;
  };
  defaultLevelRules?: ICourseRequirementRule[];
}

// TODO: This is a bit of a duplicate for now
export enum UniConfigRequirementTaskFulfilmentMethod {
  Passport = 'Passport',
  AcademicResults = 'AcademicResults',
  FinalAcademicResults = 'FinalAcademicResults',
  AcademicTranscript = 'AcademicTranscript',
  EnglishLanguageResults = 'EnglishLanguageResults',
  EnglishLanguageCertificate = 'EnglishLanguageCertificate',
  Upload = 'Upload',
  InfoOnly = 'InfoOnly',
  CalendlyInterview = 'CalendlyInterview',
  RecordedInterview = 'RecordedInterview',
  RefereeDetails = 'RefereeDetails',
  Payment = 'Payment',
}

export enum RequirementTaskInternalDataMethod {
  Upload = 'Upload',
}

export const ApplicationTaskValue = ApplicationTaskSchema.enum;

export type ApplicationTask =
  | 'EnglishLanguage'
  | 'Employment'
  | 'References'
  | 'PersonalStatement'
  | 'ResearchProposal'
  | 'Funding'
  | 'ImmigrationHistory'
  | 'DiversityInclusion'
  | 'Representation'
  | 'Referral'
  | 'Declarations';

export type ApplicationTaskConfig = Partial<Record<ApplicationTask, boolean>>;

export interface ApplicationTaskFlags {
  enabled?: boolean;
  default?: boolean;
}

export type IApplyApplicationTaskConfig = Record<ApplicationTask, ApplicationTaskFlags>;

export * from './courseTaskRules.js';
export * from './courseRequirementRules.js';
